import { base58ToBin, BaseConversionError, binToBase58, flattenBinArray, } from '../format/format';
/**
 * Base58 version byte values for common Base58Address format versions.
 */
export var Base58AddressFormatVersion;
(function (Base58AddressFormatVersion) {
    /**
     * A Pay to Public Key Hash (P2PKH) address – base58 encodes to a leading `1`.
     *
     * Hex: `0x00`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["p2pkh"] = 0] = "p2pkh";
    /**
     * A Pay to Script Hash (P2SH) address – base58 encodes to a leading `3`.
     *
     * Hex: `0x05`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["p2sh"] = 5] = "p2sh";
    /**
     * A private key in Wallet Import Format. For private keys used with
     * uncompressed public keys, the payload is 32 bytes and causes the version
     * to be encoded as a `5`. For private keys used with compressed public keys,
     * a final `0x01` byte is appended to the private key, increasing the payload
     * to 33 bytes, and causing the version to be encoded as a `K` or `L`.
     *
     * Hex: `0x80`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["wif"] = 128] = "wif";
    /**
     * A testnet Pay to Public Key Hash (P2PKH) address – base58 encodes to a
     * leading `m` or `n`.
     *
     * Hex: `0x6f`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["p2pkhTestnet"] = 111] = "p2pkhTestnet";
    /**
     * A testnet Pay to Script Hash (P2SH) address – base58 encodes to a leading
     * `2`.
     *
     * Hex: `0xc4`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["p2shTestnet"] = 196] = "p2shTestnet";
    /**
     * A private key in Wallet Import Format intended for testnet use. For private
     * keys used with uncompressed public keys, the payload is 32 bytes and causes
     * the version to be encoded as a `9`. For private keys used with compressed
     * public keys, a final `0x01` byte is appended to the private key, increasing
     * the payload to 33 bytes, and causing the version to be encoded as a `c`.
     *
     * Hex: `0xef`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["wifTestnet"] = 239] = "wifTestnet";
    /**
     * A Pay to Public Key Hash (P2PKH) address intended for use on the Bitcoin
     * Cash network – base58 encodes to a leading `C`. This version was
     * temporarily used by the Copay project before the CashAddress format was
     * standardized.
     *
     * Hex: `0x1c`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["p2pkhCopayBCH"] = 28] = "p2pkhCopayBCH";
    /**
     * A Pay to Script Hash (P2SH) address intended for use on the Bitcoin
     * Cash network – base58 encodes to a leading `H`. This version was
     * temporarily used by the Copay project before the CashAddress format was
     * standardized.
     *
     * Hex: `0x28`
     */
    Base58AddressFormatVersion[Base58AddressFormatVersion["p2shCopayBCH"] = 40] = "p2shCopayBCH";
})(Base58AddressFormatVersion || (Base58AddressFormatVersion = {}));
/**
 * Encode a payload using the Base58Address format, the original address format
 * used by the Satoshi implementation.
 *
 * Note, this method does not enforce error handling via the type system. The
 * returned string will not be a valid Base58Address if `hash` is not exactly 20
 * bytes. If needed, validate the length of `hash` before calling this method.
 *
 * @remarks
 * A Base58Address includes a 1-byte prefix to indicate the address version, a
 * variable-length payload, and a 4-byte checksum:
 *
 * `[version: 1 byte] [payload: variable length] [checksum: 4 bytes]`
 *
 * The checksum is the first 4 bytes of the double-SHA256 hash of the version
 * byte followed by the payload.
 *
 * @param sha256 - an implementation of sha256 (a universal implementation is
 * available via `instantiateSha256`)
 * @param version - the address version byte (see `Base58Version`)
 * @param payload - the Uint8Array payload to encode
 */
export const encodeBase58AddressFormat = (sha256, version, payload) => {
    const checksumBytes = 4;
    const content = Uint8Array.from([version, ...payload]);
    const checksum = sha256.hash(sha256.hash(content)).slice(0, checksumBytes);
    const bin = flattenBinArray([content, checksum]);
    return binToBase58(bin);
};
/**
 * Encode a hash as a Base58Address.
 *
 * Note, this method does not enforce error handling via the type system. The
 * returned string will not be a valid Base58Address if `hash` is not exactly 20
 * bytes. If needed, validate the length of `hash` before calling this method.
 *
 * For other standards which use the Base58Address format but have other version
 * or length requirements, use `encodeCashAddressFormat`.
 *
 * @param sha256 - an implementation of sha256 (a universal implementation is
 * available via `instantiateSha256`)
 * @param type - the type of address to encode: `p2pkh`, `p2sh`,
 * `p2pkh-testnet`, or `p2sh-testnet`
 * @param hash - the 20-byte hash to encode
 * (`RIPEMD160(SHA256(public key or bytecode))`)
 */
export const encodeBase58Address = (sha256, type, payload) => encodeBase58AddressFormat(sha256, {
    p2pkh: Base58AddressFormatVersion.p2pkh,
    'p2pkh-copay-bch': Base58AddressFormatVersion.p2pkhCopayBCH,
    'p2pkh-testnet': Base58AddressFormatVersion.p2pkhTestnet,
    p2sh: Base58AddressFormatVersion.p2sh,
    'p2sh-copay-bch': Base58AddressFormatVersion.p2shCopayBCH,
    'p2sh-testnet': Base58AddressFormatVersion.p2shTestnet,
}[type], payload);
export var Base58AddressError;
(function (Base58AddressError) {
    Base58AddressError["unknownCharacter"] = "Base58Address error: address may only contain valid base58 characters.";
    Base58AddressError["tooShort"] = "Base58Address error: address is too short to be valid.";
    Base58AddressError["invalidChecksum"] = "Base58Address error: address has an invalid checksum.";
    Base58AddressError["unknownAddressVersion"] = "Base58Address error: address uses an unknown address version.";
    Base58AddressError["incorrectLength"] = "Base58Address error: the encoded payload is not the correct length (20 bytes).";
})(Base58AddressError || (Base58AddressError = {}));
/**
 * Attempt to decode a Base58Address-formatted string. This is more lenient than
 * `decodeCashAddress`, which also validates the address version.
 *
 * Returns the contents of the address or an error message as a string.
 *
 * @param sha256 - an implementation of sha256 (a universal implementation is
 * available via `instantiateSha256`)
 * @param address - the string to decode as a base58 address
 */
export const decodeBase58AddressFormat = (sha256, address) => {
    const checksumBytes = 4;
    const bin = base58ToBin(address);
    if (bin === BaseConversionError.unknownCharacter) {
        return Base58AddressError.unknownCharacter;
    }
    const minimumBase58AddressLength = 5;
    if (bin.length < minimumBase58AddressLength) {
        return Base58AddressError.tooShort;
    }
    const content = bin.slice(0, -checksumBytes);
    const checksum = bin.slice(-checksumBytes);
    const expectedChecksum = sha256
        .hash(sha256.hash(content))
        .slice(0, checksumBytes);
    if (!checksum.every((value, i) => value === expectedChecksum[i])) {
        return Base58AddressError.invalidChecksum;
    }
    return {
        payload: content.slice(1),
        version: content[0],
    };
};
/**
 * Decode and validate a Base58Address, strictly checking the version and
 * payload length.
 *
 * For other address-like standards which closely follow the Base58Address
 * format (but have alternative version byte requirements), use
 * `decodeBase58AddressFormat`.
 *
 * @remarks
 * Because the Wallet Import Format (WIF) private key serialization format uses
 * the Base58Address format, some libraries allow WIF key decoding via the same
 * method as base58 address decoding. This method strictly accepts only
 * Base58Address types, but WIF keys can be decoded with `decodePrivateKeyWif`.
 *
 * @param sha256 - an implementation of sha256 (a universal implementation is
 * available via `instantiateSha256`)
 * @param address - the string to decode as a base58 address
 */
export const decodeBase58Address = (sha256, address) => {
    const decoded = decodeBase58AddressFormat(sha256, address);
    if (typeof decoded === 'string')
        return decoded;
    if (![
        Base58AddressFormatVersion.p2pkh,
        Base58AddressFormatVersion.p2sh,
        Base58AddressFormatVersion.p2pkhTestnet,
        Base58AddressFormatVersion.p2shTestnet,
        Base58AddressFormatVersion.p2pkhCopayBCH,
        Base58AddressFormatVersion.p2shCopayBCH,
    ].includes(decoded.version)) {
        return Base58AddressError.unknownAddressVersion;
    }
    const hash160Length = 20;
    if (decoded.payload.length !== hash160Length) {
        return Base58AddressError.incorrectLength;
    }
    return decoded;
};
//# sourceMappingURL=data:application/json;base64,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